// GoLiveRunbook — Jun 2026 (Randy).
//
// The last thing in the Go-Live wizard: every MANUAL production-launch step,
// validation and verification in one collapsed list. The wizard above automates
// what an API can automate; this covers the rest — the Dashboard-only settings,
// the production env vars, the first real-card rehearsal, and the human duty
// that has to exist before real money moves.
//
// Ticks are local to this browser on purpose: this is Randy's own checklist, not
// platform state, and it must never look "done" to anyone else.
import { useMemo, useState } from "react";
import {
  CaretRight, CheckCircle, Warning, ClipboardText, Circle, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const LS_KEY = "golive_runbook_ticks_v1";

const readTicks = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
};

// `auto` is the app's own read of reality. It never blocks and never ticks the
// box for you — it just stops the runbook from lying about what is already done.
const AutoBadge = ({ state, note }) => {
  if (!state) return null;
  const map = {
    ok: ["bg-[#E8F0E9] text-[#2F6B3A]", "verified"],
    blocked: ["bg-[#F6E2DA] text-[#A03D22]", "not yet"],
    // Sep 25, 2026 — a real, read-from-Stripe fault that is degraded rather
    // than fatal. "not yet" overstated it; "can't check" hid it.
    attention: ["bg-[#F7EEDA] text-[#8A6320]", "degraded"],
    unknown: ["bg-[#F0F1EE] text-[#6B6A68]", "can't check"],
  };
  const [cls, label] = map[state] || map.unknown;
  return (
    <span className={`text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded shrink-0 ${cls}`}
      title={note || ""}>
      {label}
    </span>
  );
};

const RunbookItem = ({ id, title, doIt, verify, auto, note, ticked, onToggle }) => (
  <li className="flex gap-2.5 py-2 border-b border-[#EFEDE7] last:border-0">
    <button
      onClick={() => onToggle(id)}
      data-testid={`runbook-tick-${id}`}
      aria-label={ticked ? `Untick ${title}` : `Tick ${title}`}
      className="mt-0.5 shrink-0 text-[#4A5D4E] hover:scale-110 transition-transform"
    >
      {ticked
        ? <CheckCircle size={16} weight="fill" />
        : <Circle size={16} weight="regular" className="text-[#C9C6BE]" />}
    </button>
    <div className={`min-w-0 flex-1 ${ticked ? "opacity-55" : ""}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[12px] text-[#1C1B1A] font-medium">{title}</span>
        <AutoBadge state={auto} note={note} />
      </div>
      <div className="text-[11px] text-[#6B6A68] leading-relaxed mt-0.5">{doIt}</div>
      {verify && (
        <div className="text-[11px] text-[#4A5D4E] leading-relaxed mt-0.5">
          <strong className="text-[#6B6A68] font-normal">Verify:</strong> {verify}
        </div>
      )}
      {note && (auto === "blocked" || auto === "attention") && (
        <div className={`text-[11px] mt-0.5 ${auto === "attention" ? "text-[#8A6320]" : "text-[#A03D22]"}`}>{note}</div>
      )}
    </div>
  </li>
);

export default function GoLiveRunbook({ status, preflight, email, imp, liveAcct }) {
  const [open, setOpen] = useState(false);
  const [ticks, setTicks] = useState(readTicks);

  const toggle = (id) => {
    setTicks((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };
  const resetTicks = () => {
    try { localStorage.removeItem(LS_KEY); } catch { /* private mode */ }
    setTicks({});
  };

  const phases = useMemo(() => {
    const buckets = status?.buckets || [];
    const profits = buckets.find((b) => b.key === "site_profits");
    const allBanks = buckets.length > 0 && buckets.every((b) => b.ba_id);
    const liveEndpoint = (preflight?.endpoints || []).some((e) => e.livemode);
    const eventsOk = preflight?.mode === "live" && preflight?.ok;
    // Sep 25, 2026 — scope is machine-readable now, so "human eyes only" is gone.
    const scopeSeen = !!preflight?.scope_verified;
    const hasConnectedScope = (preflight?.endpoints || []).some((e) => (e.scope || "").includes("connected"));
    const hasPlatformScope = (preflight?.endpoints || []).some((e) => (e.scope || "").includes("your account"));
    const isLive = status?.current_mode === "live";

    return [
      {
        key: "A", title: "Stripe account & banks", sub: "Dashboard only — no API can do these",
        items: [
          { id: "a1", title: "Activate the live Stripe account",
            doIt: "Business details, tax id, identity and a settlement bank, submitted in live mode.",
            verify: "Dashboard shows charges and payouts enabled, with no “complete your account” banner.",
            auto: liveAcct ? (liveAcct.charges_enabled && liveAcct.payouts_enabled ? "ok" : "blocked") : "unknown",
            note: liveAcct && !(liveAcct.charges_enabled && liveAcct.payouts_enabled)
              ? "The live key validated but the account cannot charge or pay out yet."
              : "Validate your live key in Step 3 and this fills itself in." },
          { id: "a2", title: "Attach all three external banks in LIVE mode",
            doIt: "Settings → Payouts → External accounts. Sandbox banks do not carry over; add the real routing/account per Relay sub-account and copy each ba_xxx into Step 2.",
            verify: "All three buckets in Step 2 show a saved ba_xxx.",
            auto: allBanks ? "ok" : "blocked",
            note: allBanks ? "" : "One or more buckets still has no ba_xxx saved." },
          { id: "a3", title: "Fill nickname + account last-4 on every bucket",
            doIt: "Financials → Platform Usage Reservoir. Site Profits especially — a profit sweep refuses to run without its last-4 on file.",
            verify: "Site Profits shows the last 4 digits of the REAL bank, not the sandbox default.",
            // "6789" is the sandbox placeholder the reservoir seeds itself with, so
            // it must not be reported as a satisfied step while still in test mode.
            auto: !profits?.account_last4 ? "blocked"
              : (profits.account_last4 === "6789" && !isLive) ? "unknown" : "ok",
            note: !profits?.account_last4
              ? "Site Profits has no account last-4 — the profit sweep is blocked."
              : (profits.account_last4 === "6789" && !isLive)
                ? "Site Profits reads ····6789, which is the sandbox placeholder. Replace it with the real bank's last-4."
                : `Site Profits ····${profits.account_last4}` },
          { id: "a4", title: "Set the statement descriptor and support contact",
            doIt: "Settings → Business → Public details. The descriptor is what customers see on their bank statement; a vague one is the single biggest cause of “I don't recognise this charge” chargebacks.",
            verify: "Descriptor reads as your brand, and the support email/phone is a monitored inbox." },
          { id: "a5", title: "Turn on Radar and review the fraud rules",
            doIt: "Radar → Rules. At minimum block high-risk payments and require CVC.",
            verify: "Radar shows “enabled” with at least the default rule set active." },
        ],
      },
      {
        key: "B", title: "Webhooks", sub: "the production source of truth for every payment",
        items: [
          { id: "b1", title: "Create the LIVE-mode “Your account” destination",
            doIt: "Workbench → Webhooks → Event destinations → Create, scoped to Events from: Your account, pointed at https://<your-production-domain>/api/webhooks/stripe. A test-mode destination does not carry over.",
            verify: "The readiness list above shows a destination marked LIVE with scope “your account”.",
            auto: liveEndpoint && (!scopeSeen || hasPlatformScope) ? "ok" : "unknown",
            note: !liveEndpoint ? "No live destination seen yet — validate your live key in Step 3 to look."
              : (scopeSeen && !hasPlatformScope)
                ? "A live destination exists but NONE is scoped to your own account, so no money event can be delivered."
                : "" },
          { id: "b2", title: "Subscribe the nine money events",
            doIt: "checkout.session.completed, checkout.session.async_payment_succeeded, checkout.session.async_payment_failed, invoice.payment_succeeded, invoice.payment_failed, customer.subscription.deleted, charge.refunded, charge.dispute.created, charge.dispute.closed. All nine belong on the Your-account destination: every charge here is a destination charge owned by the platform account.",
            verify: "Webhook readiness above reports no missing events and no scope blockers on the live account.",
            auto: eventsOk ? "ok" : "unknown",
            note: eventsOk ? "" : "Checked against the live key only — validate it in Step 3." },
          { id: "b3", title: "Create the second destination, scoped to Connected accounts",
            doIt: "Same URL, Events from: Connected accounts, carrying account.updated and nothing else. It flips each creator's charges/payouts flags and releases any payout stashed while their Connect onboarding was incomplete. Money events ticked there are never delivered.",
            verify: "Readiness above lists a second destination with scope “connected accounts”. Scope is read from Stripe's v2 event-destination API, so this is no longer a by-eye check.",
            auto: !scopeSeen ? "unknown" : hasConnectedScope ? "ok" : "attention",
            note: !scopeSeen ? "Scope could not be read on the active key — check it in the Dashboard."
              : hasConnectedScope
                ? "Note: the app holds ONE signing secret, so unless it is this destination's, its deliveries are rejected 400 and payout status falls back to polling /stripe/connect/status."
                : "No connected-accounts destination exists, so account.updated never arrives. Creator payout status only refreshes when they open their Stripe screen — slow, not broken." },
          { id: "b4", title: "Put the live signing secret in production",
            doIt: "Copy whsec_… into STRIPE_WEBHOOK_SECRET in the production environment, then paste it into Step 3 as well.",
            verify: "Webhook processing fails closed without it — every event would be rejected.",
            auto: preflight?.signing_secret_configured === false ? "blocked"
              : preflight?.signing_secret_configured ? "ok" : "unknown",
            note: preflight?.signing_secret_configured === false
              ? "No signing secret configured on the active key." : "" },
          { id: "b5", title: "Send a test event and read the delivery log",
            doIt: "Dashboard → the endpoint → “Send test webhook”, pick checkout.session.completed.",
            verify: "Stripe's delivery log shows HTTP 200. A 401 means the secret is wrong; a 502 means the endpoint URL is wrong." },
        ],
      },
      {
        key: "C", title: "Production environment", sub: "the preview pod is not production",
        items: [
          { id: "c1", title: "Unset EMAIL_DISABLED in production",
            doIt: "It is the preview kill switch. While it is on, every refund receipt, dispute notice and commission-reversal notice is dropped silently.",
            verify: "Step 3b above reports outbound sends enabled.",
            auto: email ? (email.email_disabled ? "blocked" : "ok") : "unknown",
            note: email?.email_disabled ? "EMAIL_DISABLED is still ON here." : "" },
          { id: "c2", title: "Resend key, verified sender domain and reply-to",
            doIt: "RESEND_API_KEY, a verified sending domain, and RESEND_REPLY_TO pointed at a monitored inbox.",
            verify: "All four rows in Step 3b are green.",
            auto: email
              ? (email.resend_api_key_configured && email.sender_domain_verified && email.reply_to_configured
                ? "ok" : "blocked")
              : "unknown",
            note: email && !email.sender_domain_verified
              ? "Unverified sender — Resend will only deliver to your own addresses." : "" },
          { id: "c3", title: "Put the live keys in the production environment itself",
            doIt: "Promotion writes a runtime override in the database. Also set STRIPE_SECRET_KEY / STRIPE_PUBLISHABLE_KEY in the production env so a fresh deploy or a wiped override cannot silently fall back to test keys.",
            verify: "Redeploy, then confirm the mode badge above still reads live." },
          { id: "c4", title: "Impersonation OFF",
            doIt: "Step 0 above. You promised users nobody can read their private data.",
            verify: "Step 0 shows the green state.",
            auto: imp ? (imp.impersonation_enabled ? "blocked" : "ok") : "unknown",
            note: imp?.impersonation_enabled ? "Impersonation is still ON." : "" },
          { id: "c5", title: "Recreate your prices on the LIVE account",
            doIt: "Price objects do not cross modes. Check the creator monthly fee, the Creator Pro gate and every voucher pack against the dials in Pricing Settings.",
            verify: "Open a real checkout for each product and confirm the amount matches the dial exactly." },
          { id: "c6", title: "Back up the database before promoting",
            doIt: "Take a snapshot you can actually restore. Promotion is reversible; a bad first day of live data is not.",
            verify: "You have a restore command you have actually run once." },
          { id: "c7", title: "Set PLATFORM_MAILING_ADDRESS",
            doIt: "Commercial email legally needs a physical postal address in the footer. A street address, a USPS-registered PO Box, or a virtual mailbox (PMB — needs USPS Form 1583) all qualify. Never use your home address; it is public forever once it ships. Creator broadcasts are BLOCKED until this is set.",
            verify: "Send yourself a broadcast test and read the footer — your real address, not placeholder text.",
            auto: email ? (email.mailing_address_configured ? "ok" : "blocked") : "unknown",
            note: email && !email.mailing_address_configured
              ? "Not set — creator broadcasts are refusing to send." : "" },
          { id: "c8", title: "Wire the Resend bounce & complaint webhook",
            doIt: "Resend → Webhooks → add an endpoint at /api/webhooks/resend subscribed to email.bounced and email.complained, then put its signing secret in RESEND_WEBHOOK_SECRET. Hard bounces then suppress the address permanently and spam complaints stop promotional mail while receipts keep flowing.",
            verify: "Resend's delivery log shows HTTP 200 on a test event, and Communications → Platform email catalog reports handling live.",
            auto: email ? (email.bounce_webhook_configured ? "ok" : "blocked") : "unknown",
            note: email && !email.bounce_webhook_configured
              ? "No bounce handling at all — a dead address will be mailed forever." : "" },
          { id: "c9", title: "Send from your own brand, and add DMARC",
            doIt: "The sender domain should match the site customers are on, or Gmail reads it as a phishing signal. Verify a My Silo sending subdomain in Resend, add its SPF + DKIM, add a _dmarc TXT record (start at p=none), and point RESEND_REPLY_TO at a monitored inbox on the same brand.",
            verify: "Send yourself one email and check the raw headers: SPF pass, DKIM pass, DMARC pass, and a From: address a customer would recognise.",
            auto: email ? (email.sender_problem ? "blocked" : "ok") : "unknown",
            note: email?.sender_problem || (email?.sender ? `Currently sending as ${email.sender}.` : "") },
          { id: "c10", title: "Prove every email can actually send",
            doIt: "Communications → Platform email catalog → “Send every email to me”. It fires one of every catalogued email at your inbox with sample data, so you are not discovering a broken template during a real refund.",
            verify: "The result card reports zero failures, and the launch-day check's email-coverage row goes green." },
          { id: "c11", title: "Make the published support address actually receive mail",
            doIt: "Every legal page points DMCA notices, GDPR/CCPA requests, arbitration opt-outs and billing-error claims at one address (frontend/src/lib/contact.js + SUPPORT_EMAIL). easttactics.com is already on Cloudflare with Email Routing MX in place, so this is one rule, no DNS change: Cloudflare → easttactics.com → Email → Email Routing → Destination addresses → add your Gmail and click its verification link → Custom addresses → Create → support → Send to an email → your Gmail. Add a catch-all too so nothing is ever silently rejected.",
            verify: "Send a real email to the published address from your phone and confirm it lands in your inbox. A 550 'Address does not exist' means the rule is not live.",
            auto: "ok",
            note: "Verified live by SMTP probe Sep 2026: support@, info@, help@, legal@, billing@, privacy@, dmarc@ and a random control all accepted (catch-all on). Every one of them returned 550 before the rules existed." },
          { id: "c12", title: "Point RESEND_REPLY_TO at that same working mailbox",
            doIt: "Set RESEND_REPLY_TO to the published support address once c11 is verified. It is the reply-to header on every outbound email, so it is where every customer reply lands.",
            verify: "Reply to one of your own platform emails and confirm the reply arrives.",
            auto: email ? (email.reply_to_configured ? "ok" : "blocked") : "unknown",
            note: "A reply-to that does not exist bounces back to the CUSTOMER, not to you — so this fails invisibly from your side." },
          { id: "c13", title: "Set the 1099 payer identity",
            doIt: "PAYER_LEGAL_NAME, PAYER_ADDRESS_LINE1, PAYER_ADDRESS_LINE2 and PAYER_EIN print on every 1099-NEC a creator receives. The name must be the same legal entity your Terms name and the one that owns the Stripe account — a 1099 from a company the recipient never contracted with is their problem to explain to the IRS. Generation is refused until all four are real.",
            verify: "Super Admin → launch-day check → “1099 payer identity is real” is green, and it names your entity and address.",
            auto: email ? (email.tax_payer_problem ? "blocked" : "ok") : "unknown",
            note: email?.tax_payer_problem
              || (email?.tax_payer_name ? `1099s will be issued by ${email.tax_payer_name}.` : "") },
        ],
      },
      {
        key: "D", title: "Promote", sub: "the flip itself",
        items: [
          { id: "d1", title: "Validate the live keys (read-only)",
            doIt: "Step 3 above. Nothing is committed — it reads the account back so you can eyeball the business name, country and balance before you commit.",
            verify: "You see the green “Live key valid” card with the right account id." },
          { id: "d2", title: "Promote to live",
            doIt: "Step 4: type GO-LIVE-CONFIRMED and fire. Impersonation and the live webhook check both block this on purpose.",
            verify: "The badge next to “Go-Live wizard” reads live.",
            auto: isLive ? "ok" : "blocked",
            note: isLive ? "" : "Still in test mode." },
          { id: "d3", title: "Write down the rollback id",
            doIt: "The toast shows it, and the audit history keeps it. You want it in your notes, not in a toast you dismissed.",
            verify: "The rollback id is saved somewhere outside this app." },
        ],
      },
      {
        key: "E", title: "First-real-money rehearsal",
        sub: "your own card, small amounts, then refund yourself",
        items: [
          { id: "e1", title: "Buy the creator subscription with a real card",
            doIt: "Use a referral link so the rev-share path runs too.",
            verify: "Creator access flips on, the payment shows completed with a charge id, and the referrer's ledger shows a commission row carrying that charge id." },
          { id: "e2", title: "Buy Creator Pro",
            doIt: "Ideally as a referred account, so the commission linkage is exercised end to end.",
            verify: "The SKUs appear, and the referrer's commission row carries the real charge id — the refund clawback depends entirely on that field being populated." },
          { id: "e3", title: "Buy a voucher top-up",
            doIt: "The smallest pack is enough.",
            verify: "A credit lot is minted and the balance moves by exactly the pack amount." },
          { id: "e4", title: "Refund the Creator Pro charge from the Stripe Dashboard",
            doIt: "Full refund. This is the single most important rehearsal — it exercises the whole reversal chain.",
            verify: "Access is revoked, the referrer's commission is reversed, both of you get an email, and a review item opens in Financials → Refunds, disputes & review." },
          { id: "e5", title: "Partially refund the voucher top-up",
            doIt: "Refund about half.",
            verify: "Only the unspent portion is clawed back, the lot shows the reversed amount, and the customer gets the refund receipt." },
          { id: "e6", title: "Confirm the first payout reaches each bank",
            doIt: "Either wait for Stripe's first real payout or run one 1¢ validation per bucket in Step 2. In live mode with a balance, that 1¢ genuinely moves.",
            verify: "Each of the three banks shows a paid or in-transit payout in the Dashboard." },
          { id: "e7", title: "Replay anything Stripe could not deliver",
            doIt: "If any delivery in the log shows a non-200, use the webhook replay tool in Financials rather than re-triggering from Stripe.",
            verify: "Every event in the delivery log for launch day reads 200." },
          { id: "e8", title: "Run the one-click launch-day check",
            doIt: "Step 5 above, once the purchases and refunds are done. It reads the evidence for every step in this phase and names anything you have not exercised yet.",
            verify: "The verdict reads green, and every chip under “Rehearsal coverage” is ticked." },
        ],
      },
      {
        key: "F", title: "Standing duty", sub: "assign this to a human before you launch",
        items: [
          { id: "f1", title: "Name who answers disputes",
            doIt: "Evidence deadlines are hard. The Review queue shows the dispute playbook and a countdown per case, but somebody has to open it.",
            verify: "A named person, and they have logged into Financials → Refunds, disputes & review at least once." },
          { id: "f2", title: "Set a daily review-queue check",
            doIt: "Partial refunds, unrecovered affiliate commissions and destination-charge refunds all land there as review items rather than moving money automatically.",
            verify: "A recurring calendar entry exists." },
          { id: "f3", title: "Point Stripe's dispute alerts at a monitored inbox",
            doIt: "Dashboard → Settings → Notifications. Stripe emails you the moment a dispute opens; that is usually earlier than any in-app signal.",
            verify: "A test notification arrives at the intended inbox." },
          { id: "f4", title: "Decide the write-off policy for unrecovered commissions",
            doIt: "When a refund lands after an affiliate has already been paid, the platform records it as unrecovered and never debits the creator. Someone still has to decide whether you write it off or ask for it back.",
            verify: "The policy is written down, and Terms still matches what the code does." },
        ],
      },
      {
        key: "G", title: "If it goes wrong",
        sub: "read this before you need it",
        items: [
          { id: "g1", title: "Roll back to sandbox",
            doIt: "The rollback button appears in Step 4 once live. It clears the live keys and restores test mode.",
            verify: "The mode badge returns to test. Note that money already taken stays in Stripe — refund those charges from the Dashboard yourself." },
          { id: "g2", title: "Know where the money is",
            doIt: "Rollback does not reverse charges, payouts or subscriptions. Stripe is the ledger of record; this app mirrors it.",
            verify: "You can find a given charge in the Dashboard from an in-app payment row, and back again." },
        ],
      },
    ];
  }, [status, preflight, email, imp, liveAcct]);

  const allItems = phases.flatMap((p) => p.items);
  const done = allItems.filter((i) => ticks[i.id]).length;
  const blocked = allItems.filter((i) => i.auto === "blocked" && !ticks[i.id]).length;

  return (
    <div className="bg-white border hairline rounded-lg" data-testid="golive-runbook">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="golive-runbook-toggle"
        aria-expanded={open}
        className="w-full flex items-center gap-2.5 px-4 py-3.5 text-left hover:bg-[#FDFCF8] transition-colors rounded-lg"
      >
        <CaretRight
          size={14} weight="bold"
          className={`text-[#6B6A68] shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
        />
        <ClipboardText size={16} weight="duotone" className="text-[#4A5D4E] shrink-0" />
        <span className="font-serif-display text-base text-[#1C1B1A]">Production go-live runbook</span>
        <span className="text-[11px] text-[#6B6A68] ml-auto shrink-0 flex items-center gap-2"
          data-testid="golive-runbook-progress">
          {blocked > 0 && (
            <span className="text-[#A03D22] flex items-center gap-1">
              <Warning size={11} weight="duotone" />{blocked} not ready
            </span>
          )}
          <span className="tabular-nums">{done}/{allItems.length}</span>
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-5 border-t border-[#EFEDE7] pt-4"
          data-testid="golive-runbook-body">
          <p className="text-[11px] text-[#6B6A68] leading-relaxed">
            Every step the wizard above cannot do for you. Ticks are saved in this browser only —
            they are your notes, not platform state. Where the app can genuinely see whether
            something is done, it says so; where Stripe hides the setting, it says that too instead
            of guessing.
          </p>

          {phases.map((phase) => (
            <div key={phase.key} data-testid={`golive-runbook-phase-${phase.key}`}>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#4A5D4E] font-medium">
                  {phase.key} · {phase.title}
                </span>
                <span className="text-[10px] text-[#9C9A94] italic">{phase.sub}</span>
              </div>
              <ul className="border hairline rounded-md px-3 bg-[#FDFCF8]">
                {phase.items.map((item) => (
                  <RunbookItem key={item.id} {...item}
                    ticked={!!ticks[item.id]} onToggle={toggle} />
                ))}
              </ul>
            </div>
          ))}

          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="text-[11px] text-[#6B6A68] italic">
              Nothing here fires an API call. Ticking a box records that you did the thing; it does
              not do the thing.
            </p>
            <button
              onClick={resetTicks}
              data-testid="golive-runbook-reset"
              className="text-[10px] px-2.5 py-1.5 rounded border hairline hover:border-[#6B6A68] flex items-center gap-1.5 shrink-0 text-[#6B6A68]"
            ><ArrowCounterClockwise size={11} /> Clear my ticks</button>
          </div>
        </div>
      )}
    </div>
  );
}
